<template>
  <FlexCol class="relative" gap-1 ref="triggerRef">
    <Label :label="label" :required="required" :error="error" v-if="label" />
    <Input v-model="localValue" :error="error" :placeholder="placeholder ? placeholder : 'Selecione a data'" :disabled="props.disabled" mask="##/##/####">
      <template #pre-icon>
        <IconInput :disabled="disabled" icon="fa-calendar-days" @click="toggle" class="border-r border-slate-300" />
      </template>
      <template #pos-icon>
        <IconInput :disabled="disabled" icon="fa-xmark" class="border-l border-slate-300" @click="clear()" v-if="localValue" />
      </template>
    </Input>
    <Error :message="error" v-if="error" />
    <Teleport to="body">
      <div v-if="open" data-floating-root="true" :style="popupStyle" class="fixed z-[2000]">
        <Calendar v-model="date" />
      </div>
    </Teleport>
  </FlexCol>
</template>

<script setup>
import { watch, ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useUtils } from '@/composables/useUtils'
import { useFormat } from '@/composables/useFormat'
const { isValidDateBR } = useUtils()
const { formatDate, formatIsoDatetime, formatBrDateToIso } = useFormat()

const props = defineProps({
  label: String,
  required: Boolean,
  disabled: Boolean,
  error: Array,
  modelValue: [String, Number],
  placeholder: String,
  dateOnly: Boolean,
})

const emits = defineEmits(['update:modelValue', 'calendar-toggle'])

const normalizeDateOnly = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    const isoDate = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoDate) {
      return `${isoDate[1]}-${isoDate[2]}-${isoDate[3]}`
    }

    const brDate = trimmedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (brDate) {
      return `${brDate[3]}-${brDate[2]}-${brDate[1]}`
    }
  }

  const parsedDate = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return null

  const year = parsedDate.getFullYear()
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
  const day = String(parsedDate.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatDateOnly = (value) => {
  const normalizedValue = normalizeDateOnly(value)
  if (!normalizedValue) return null

  const [year, month, day] = normalizedValue.split('-')
  return `${day}/${month}/${year}`
}

const formatInputDate = (value) => (props.dateOnly ? formatDateOnly(value) : formatDate(value))
const formatOutputDate = (value) => (props.dateOnly ? normalizeDateOnly(value) : formatIsoDatetime(value))

const localValue = ref(props.modelValue ? formatInputDate(props.modelValue) : null)

const error = ref(props.error)
const open = ref(false)
const isFocus = ref(false)
const date = ref(props.modelValue)
const triggerRef = ref(null)
const popupStyle = ref({})

const updatePopupPosition = () => {
  const triggerElement = triggerRef.value?.$el ?? triggerRef.value
  if (!open.value || !triggerElement || typeof triggerElement.getBoundingClientRect !== 'function') return
  const rect = triggerElement.getBoundingClientRect()
  popupStyle.value = {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  }
}

const toggle = () => {
  if (!props.disabled) {
    open.value = !open.value
    if (open.value) {
      nextTick(updatePopupPosition)
    }
    emits('calendar-toggle', open.value)
  }
}

const clear = () => {
  if (!props.disabled) {
    localValue.value = null
    date.value = null
    emits('update:modelValue', null)
    return toggle()
  }
}

onMounted(() => {
  if (props.modelValue) {
    emits('update:modelValue', formatOutputDate(props.modelValue))
  }
  window.addEventListener('resize', updatePopupPosition)
  window.addEventListener('scroll', updatePopupPosition, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePopupPosition)
  window.removeEventListener('scroll', updatePopupPosition, true)
})

watch(date, (newValue) => {
  emits('update:modelValue', formatOutputDate(newValue))
  return toggle()
})

watch(open, (isOpen) => {
  if (isOpen) {
    nextTick(updatePopupPosition)
  }
})

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      emits('update:modelValue', formatOutputDate(newValue))
    }
    localValue.value = formatInputDate(newValue)
  },
  { immediate: true, deep: true },
)

watch(
  () => props.error,
  (newValue) => {
    error.value = newValue
  },
)

watch(
  () => localValue.value,
  (newValue) => {
    if (newValue && newValue.length === 10 && isValidDateBR(newValue)) {
      const rawValue = props.dateOnly ? normalizeDateOnly(newValue) : formatBrDateToIso(newValue)

      if (rawValue) {
        emits('update:modelValue', formatOutputDate(rawValue))
      }
    }
  },
)
</script>
